import { useCallback, useEffect, useRef, useState } from "react";
import { toSavedIndicator } from "../settings/savedIndicators";

/**
 * Indicators of the chart. The engine keeps the list: the hook puts the
 * saved indicators on a new engine, then follows engine.events.onIndicatorsChange
 * and saves every change to the chart settings (they survive F5).
 *
 * @param {object|null} engine - ChartEngine, null until it is created
 * @param {{ settings: object, updateSettings: Function }} chartSettingsApi - useChartSettings
 * @returns {{ indicators: object[], add: Function, remove: Function, removeAll: Function,
 *   setVisible: Function, update: Function, setPaneExpanded: Function }} indicators: engine.getIndicators() items
 */
const useIndicators = (engine, chartSettingsApi) => {
  const { settings, updateSettings } = chartSettingsApi;
  const [indicators, setIndicators] = useState([]);

  // Read once per engine: the saved list changes with every engine event
  const savedIndicatorsRef = useRef(settings.indicators);

  savedIndicatorsRef.current = settings.indicators;

  useEffect(() => {
    if (!engine) return undefined;

    savedIndicatorsRef.current.forEach(({ id, inputs, styles, isVisible }) => {
      const key = engine.addIndicator(id, inputs, styles);

      if (key && !isVisible) engine.setIndicatorVisible(key, false);
    });

    // The listener gets the restored list at once
    const unsubscribe = engine.events.onIndicatorsChange((list) => {
      setIndicators(list);
      updateSettings({ indicators: list.map(toSavedIndicator) });
    });

    return () => {
      unsubscribe();
      setIndicators([]);
    };
  }, [engine, updateSettings]);

  const add = useCallback((id) => engine?.addIndicator(id), [engine]);

  const remove = useCallback((key) => engine?.removeIndicator(key), [engine]);

  const removeAll = useCallback(() => engine?.removeAllIndicators(), [engine]);

  const setVisible = useCallback((key, isVisible) => engine?.setIndicatorVisible(key, isVisible), [engine]);

  const update = useCallback((key, changes) => engine?.updateIndicator(key, changes), [engine]);

  const setPaneExpanded = useCallback(
    (key, isExpanded) => engine?.setPaneExpanded(key, isExpanded),
    [engine],
  );

  return { indicators, add, remove, removeAll, setVisible, update, setPaneExpanded };
};

export default useIndicators;
